import { DataContext } from '@/providers/DataProviders/DataProvider';
import { useState, useEffect, useCallback, useContext } from 'react';

// Custom hook for step-wise IndexedDB storage
export const useStepwiseStorage = (flowName = 'manualOrder', version = 1) => {
    const { userId } = useContext(DataContext);
    const [db, setDb] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const dbName = `${flowName}_stepwise_data`;
    const storeName = 'flowSteps';

    // Initialize IndexedDB
    useEffect(() => {
        const initDB = async () => {
            try {
                const request = indexedDB.open(dbName, version);

                request.onerror = () => {
                    setError('Failed to open database');
                    setLoading(false);
                };

                request.onsuccess = (event) => {
                    setDb(event.target.result);
                    setLoading(false);
                };

                request.onupgradeneeded = (event) => {
                    const database = event.target.result;

                    // Create object store for step data
                    if (!database.objectStoreNames.contains(storeName)) {
                        const store = database.createObjectStore(storeName, {
                            keyPath: 'stepId'
                        });

                        // Create indexes for better querying
                        store.createIndex('flowId', 'flowId', { unique: false });
                        store.createIndex('stepNumber', 'stepNumber', { unique: false });
                        store.createIndex('timestamp', 'timestamp', { unique: false });
                    }
                };
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };

        initDB();
    }, [dbName, version]);

    useEffect(() => {
        clearAllFlowData(userId)
    }, [db])

    // Save step data
    const saveStepData = useCallback(async (stepNumber, data, userId = 'default') => {
        if (!db) return null;

        return new Promise((resolve, reject) => {
            try {
                const transaction = db.transaction([storeName], 'readwrite');
                const store = transaction.objectStore(storeName);
                // console.log(data)
                const stepData = {
                    stepId: `${flowName}_${userId}_step_${stepNumber}`,
                    flowId: `${flowName}_${userId}`,
                    stepNumber,
                    data,
                    timestamp: new Date().toISOString(),
                    userId,
                    completed: true
                };

                const request = store.put(stepData);

                request.onsuccess = () => {
                    // console.log(`Step ${stepNumber} data saved successfully`);
                    resolve(stepData);
                };

                request.onerror = () => reject(request.error);

                transaction.onerror = () => reject(transaction.error);
            } catch (err) {
                reject(err);
            }
        });
    }, [db, flowName, storeName]);

    // Get step data
    const getStepData = useCallback(async (stepNumber, userId = 'default') => {
        if (!db) return null;

        return new Promise((resolve, reject) => {
            try {
                const transaction = db.transaction([storeName], 'readonly');
                const store = transaction.objectStore(storeName);
                const stepId = `${flowName}_${userId}_step_${stepNumber}`;
                const request = store.get(stepId);

                request.onsuccess = () => {
                    resolve(request.result || null);
                };

                request.onerror = () => reject(request.error);
            } catch (err) {
                reject(err);
            }
        });
    }, [db, flowName, storeName]);

    // Get all flow data
    const getAllFlowData = useCallback(async (userId = 'default') => {
        if (!db) return [];

        return new Promise((resolve, reject) => {
            try {
                const transaction = db.transaction([storeName], 'readonly');
                const store = transaction.objectStore(storeName);
                const index = store.index('flowId');
                const flowId = `${flowName}_${userId}`;
                const request = index.getAll(flowId);

                request.onsuccess = () => {
                    const sortedData = request.result.sort((a, b) => a.stepNumber - b.stepNumber);
                    resolve(sortedData);
                };

                request.onerror = () => reject(request.error);
            } catch (err) {
                reject(err);
            }
        });
    }, [db, flowName, storeName]);

    // Clear step data
    const clearStepData = useCallback(async (stepNumber, userId = 'default') => {
        if (!db) return null;

        return new Promise((resolve, reject) => {
            try {
                const transaction = db.transaction([storeName], 'readwrite');
                const store = transaction.objectStore(storeName);
                const stepId = `${flowName}_${userId}_step_${stepNumber}`;
                const request = store.delete(stepId);

                request.onsuccess = () => {
                    // console.log(`Step ${stepNumber} data cleared`);
                    resolve(true);
                };

                request.onerror = () => reject(request.error);
            } catch (err) {
                reject(err);
            }
        });
    }, [db, flowName, storeName]);

    // Clear all flow data
    const clearAllFlowData = useCallback(async (userId = 'default') => {
        if (!db) return null;

        return new Promise((resolve, reject) => {
            try {
                const transaction = db.transaction([storeName], 'readwrite');
                const store = transaction.objectStore(storeName);

                // Use cursor to iterate through ALL records and delete those matching flowName pattern
                // This handles cases where userId format might have changed between save and clear
                const request = store.openCursor();
                let deletedCount = 0;

                request.onsuccess = (event) => {
                    const cursor = event.target.result;
                    if (cursor) {
                        const record = cursor.value;
                        // Check if flowId starts with flowName_ (e.g., "manualOrder_")
                        // This will match regardless of userId format
                        if (record.flowId && record.flowId.startsWith(`${flowName}_`)) {
                            const deleteRequest = cursor.delete();
                            deleteRequest.onsuccess = () => {
                                deletedCount++;
                                cursor.continue();
                            };
                            deleteRequest.onerror = () => {
                                reject(deleteRequest.error);
                            };
                        } else {
                            // Skip records that don't match
                            cursor.continue();
                        }
                    } else {
                        // No more records, all matching records deleted
                        resolve(deletedCount);
                    }
                };

                request.onerror = () => {
                    reject(request.error);
                };

                transaction.onerror = () => {
                    reject(transaction.error);
                };
            } catch (err) {
                reject(err);
            }
        });
    }, [db, flowName, storeName]);

    // Get completed steps
    const getCompletedSteps = useCallback(async (userId = 'default') => {
        if (!db) return [];

        try {
            const allData = await getAllFlowData(userId);
            return allData
                .filter(step => step.completed)
                .map(step => step.stepNumber)
                .sort((a, b) => a - b);
        } catch (err) {
            console.error('Error getting completed steps:', err);
            return [];
        }
    }, [getAllFlowData]);

    // Check if step is completed
    const isStepCompleted = useCallback(async (stepNumber, userId = 'default') => {
        try {
            const stepData = await getStepData(stepNumber, userId);
            return stepData ? stepData.completed : false;
        } catch (err) {
            console.error('Error checking step completion:', err);
            return false;
        }
    }, [getStepData]);

    // Update step data (merge with existing)
    const updateStepData = useCallback(async (stepNumber, newData, userId = 'default') => {
        try {
            const existingData = await getStepData(stepNumber, userId);
            const mergedData = existingData
                ? { ...existingData.data, ...newData }
                : newData;

            return await saveStepData(stepNumber, mergedData, userId);
        } catch (err) {
            console.error('Error updating step data:', err);
            throw err;
        }
    }, [getStepData, saveStepData]);

    return {
        loading,
        error,
        saveStepData,
        getStepData,
        getAllFlowData,
        clearStepData,
        clearAllFlowData,
        getCompletedSteps,
        isStepCompleted,
        updateStepData,
        isReady: !loading && !error
    };
};